import PageTitle from "../../utils/PageTitle";
import { Coins } from "lucide-react";
import { useState, useEffect } from "react";
import { Table, Typography, Card, Row, Col, Divider, Button, DatePicker, Input, Space, Tag, Modal, Select } from "antd";
import PageWrapper from "../../utils/PageWrapper";
import { EyeOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth } from "../../../components/layout/auth";
import { getStatusTag } from "../../../components/views/dashboard/ListDrawer";
import { Link } from "react-router-dom";
import VerificacionPagosOverview from "./VerificacionPagosOverview";
import AddPagoForm from "./AddPagoForm";
import ListOfPagosByVerification from "./ListOfPagosByVerification";
import moment from "moment";

const { Title, Text } = Typography;

export default function PagosPorVerificacion() {
  const [verificaciones, setVerificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddPagoModalVisible, setIsAddPagoModalVisible] = useState(false);
  const [initialVerificacionIdForAddPago, setInitialVerificacionIdForAddPago] = useState(null);
  const [addPagoLoading, setAddPagoLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredVerificaciones, setFilteredVerificaciones] = useState([]);
  const [dateRange, setDateRange] = useState([moment().startOf("year"), moment().endOf("year")]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedVerificacionId, setSelectedVerificacionId] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    "Control Interno",
    "Status Verificación",
    "Monto Verificación",
    "Monto Pagado",
    "Balance",
    "Acciones",
  ]);
  const auth = useAuth();

  const fetchVerificaciones = async () => {
    try {
      const [startDate, endDate] = dateRange;
      const params = { u: auth.UID, rol: auth.Rol, startDate: startDate.format("YYYY-MM-DD"), endDate: endDate.format("YYYY-MM-DD") };
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/verificaciones-pagos-summary`, { params });
      setVerificaciones(res.data);
    } catch (err) {
      console.error("Error fetching verificaciones payment summary:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.UID) {
      fetchVerificaciones();
    }
  }, [auth?.UID, auth?.Rol, dateRange]);

  useEffect(() => {
    if (search) {
      const filtered = verificaciones.filter((verificacion) => {
        return (
          verificacion?.ControlInterno?.toString().toLowerCase().includes(search.toLowerCase()) ||
          verificacion?.MontoVerificacion?.toString().toLowerCase().includes(search.toLowerCase()) ||
          verificacion?.MontoPagado?.toString().toLowerCase().includes(search.toLowerCase()) ||
          verificacion?.Balance?.toString().toLowerCase().includes(search.toLowerCase())
        );
      });
      setFilteredVerificaciones(filtered);
    } else {
      setFilteredVerificaciones(verificaciones);
    }
  }, [search, verificaciones]);

  const getTagColor = (status) => {
    switch (status) {
      case 1:
        return "blue";
      case 2:
        return "green";
      case 3:
        return "red";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "Pendiente";
      case 2:
        return "Confirmado";
      case 3:
        return "Cancelado";
      default:
        return "Desconocido";
    }
  };

  const showAddPaymentModal = (verificacionId) => {
    setInitialVerificacionIdForAddPago(verificacionId);
    setIsAddPagoModalVisible(true);
  };

  const handlePaymentAdded = () => {
    fetchVerificaciones(); // Re-fetch data after payment is added
  };

  const allColumns = [
    {
      title: "Control Interno",
      dataIndex: "ControlInterno",
      key: "ControlInterno",
      render: (text, record) => {
        if (auth.Rol === "director") {
          return <Link to={`/verificaciones/${record.VerificacionID}`}>{text}</Link>;
        }
        return text;
      },
    },
    {
      title: "Status Verificación",
      dataIndex: "Status",
      key: "Status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Monto Verificación",
      dataIndex: "MontoVerificacion",
      key: "MontoVerificacion",
      align: "right",
      render: (text) => `${Number(text).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: "Monto Pagado",
      dataIndex: "MontoPagado",
      key: "MontoPagado",
      align: "right",
      render: (text) => `${Number(text).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: "Balance",
      dataIndex: "Balance",
      key: "Balance",
      align: "right",
      render: (text) => {
        const balance = Number(text);
        return balance !== 0 ? (
          <Tag color="red">{balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Tag>
        ) : (
          balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (text, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedVerificacionId(record.VerificacionID);
              setIsModalVisible(true);
            }}
          />
          <Button type="primary" onClick={() => showAddPaymentModal(record.VerificacionID)} icon={<Coins />} />
        </Space>
      ),
    },
  ];

  const columns = allColumns.filter((column) => visibleColumns.includes(column.title));

  const totalMontoVerificaciones = filteredVerificaciones.reduce((acc, curr) => acc + Number(curr.MontoVerificacion), 0);
  const totalMontoPagado = filteredVerificaciones.reduce((acc, curr) => acc + Number(curr.MontoPagado), 0);
  const totalBalance = filteredVerificaciones.reduce((acc, curr) => acc + Number(curr.Balance), 0);

  return (
    <PageWrapper className="pagos-main-card">
      <PageTitle icon={<Coins />} title="Pagos por Verificación" />
      <div
        style={{
          padding: "16px",
          background: "rgba(249, 250, 251, 0.8)",
          borderRadius: "12px",
          border: "1px solid rgba(229, 231, 235, 0.6)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
          marginBottom: "24px",
        }}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <DatePicker.RangePicker value={dateRange} onChange={(dates) => setDateRange(dates)} style={{ width: "100%" }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input.Search
              placeholder="Buscar..."
              onSearch={(value) => setSearch(value)}
              enterButton
              loading={loading}
              style={{ width: "100%" }}
            />
          </Col>
          {/* <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Seleccionar columnas"
              defaultValue={["Status Verificación", "Balance"]}
              onChange={(values) => setVisibleColumns(["Control Interno", "Monto Verificación", "Monto Pagado", "Acciones", ...values])}
              maxTagCount={2}
            >
              <Select.Option key="Status" value="Status Verificación">
                Status Verificación
              </Select.Option>
              <Select.Option key="Balance" value="Balance">
                Balance
              </Select.Option>
            </Select>
          </Col> */}
        </Row>
      </div>
      <VerificacionPagosOverview
        totalMontoVerificaciones={totalMontoVerificaciones}
        totalMontoPagado={totalMontoPagado}
        totalBalance={totalBalance}
      />

      <Table dataSource={filteredVerificaciones} columns={columns} loading={loading} rowKey="VerificacionID" />
      <AddPagoForm
        visible={isAddPagoModalVisible}
        setVisible={setIsAddPagoModalVisible}
        initialVerificacionId={initialVerificacionIdForAddPago}
        onPaymentAdded={handlePaymentAdded}
        loading={addPagoLoading}
        setLoading={setAddPagoLoading}
      />
      <Modal
        title="Pagos para la Verificación"
        visible={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedVerificacionId && <ListOfPagosByVerification verificacionId={selectedVerificacionId} />}
      </Modal>
    </PageWrapper>
  );
}
